import React from "react";
import {
  EducationExample,
  WorkExperienceExample,
  CustomButtonsExample,
} from "../../../../components/FormFields/LoopFieldsSet.example";
import {
  LoopFieldsSet,
  FormFieldSet,
  Button,
  useFormHandler,
} from "../../../../components";
import { v4 as uuidv4 } from "uuid";

const LoopFormFieldsList = () => {
  // Example 4: Minimal Configuration
  const MinimalExample = () => {
    const initialFormData = {
      items: [{ id: uuidv4(), name: "" }],
    };

    const { formData, handleChange, setFormData } = useFormHandler(
      initialFormData,
      {}
    );

    const addItem = () => {
      setFormData({
        ...formData,
        items: [...formData.items, { id: uuidv4(), name: "" }],
      });
    };

    const removeItem = (id) => {
      const updated = formData.items.filter((item) => item.id !== id);
      setFormData({ ...formData, items: updated });
    };

    return (
      <LoopFieldsSet
        title="Simple Items"
        data={formData.items}
        onAdd={addItem}
        onRemove={removeItem}
        renderFields={(item, index) => (
          <FormFieldSet
            type="text"
            title="Item Name"
            value={item.name || ""}
            onChange={(value) => handleChange(`items[${index}].name`, value)}
          />
        )}
      />
    );
  };

  // Example 5: Header-only Buttons
  const HeaderButtonsExample = () => {
    const initialFormData = {
      contacts: [{ id: uuidv4(), name: "", email: "" }],
    };

    const { formData, handleChange, setFormData } = useFormHandler(
      initialFormData,
      {}
    );

    const addContact = () => {
      setFormData({
        ...formData,
        contacts: [...formData.contacts, { id: uuidv4(), name: "", email: "" }],
      });
    };

    const removeContact = (id) => {
      const updated = formData.contacts.filter((item) => item.id !== id);
      setFormData({ ...formData, contacts: updated });
    };

    return (
      <LoopFieldsSet
        title="Emergency Contacts"
        data={formData.contacts}
        onAdd={addContact}
        onRemove={removeContact}
        addRemoveButtonsPosition="header"
        minItems={1}
        maxItems={5}
        addButtonTitle="Add Emergency Contact"
        gridCols="grid-cols-2"
        renderFields={(contact, index) => (
          <>
            <FormFieldSet
              type="text"
              title="Contact Name"
              value={contact.name || ""}
              onChange={(value) =>
                handleChange(`contacts[${index}].name`, value)
              }
            />
            <FormFieldSet
              type="email"
              title="Email Address"
              value={contact.email || ""}
              onChange={(value) =>
                handleChange(`contacts[${index}].email`, value)
              }
            />
          </>
        )}
      />
    );
  };

  // Example 6: No Built-in Buttons with Custom Actions
  const CustomActionsExample = () => {
    const initialFormData = {
      tasks: [
        { id: uuidv4(), title: "", priority: "medium", completed: false },
      ],
    };

    const { formData, handleChange, setFormData } = useFormHandler(
      initialFormData,
      {}
    );

    const addTask = () => {
      setFormData({
        ...formData,
        tasks: [
          ...formData.tasks,
          { id: uuidv4(), title: "", priority: "medium", completed: false },
        ],
      });
    };

    const removeTask = (id) => {
      const updated = formData.tasks.filter((item) => item.id !== id);
      setFormData({ ...formData, tasks: updated });
    };

    const toggleComplete = (id) => {
      const updated = formData.tasks.map((task) =>
        task.id === id ? { ...task, completed: !task.completed } : task
      );
      setFormData({ ...formData, tasks: updated });
    };

    const moveUp = (index) => {
      if (index > 0) {
        const updated = [...formData.tasks];
        [updated[index - 1], updated[index]] = [
          updated[index],
          updated[index - 1],
        ];
        setFormData({ ...formData, tasks: updated });
      }
    };

    const moveDown = (index) => {
      if (index < formData.tasks.length - 1) {
        const updated = [...formData.tasks];
        [updated[index], updated[index + 1]] = [
          updated[index + 1],
          updated[index],
        ];
        setFormData({ ...formData, tasks: updated });
      }
    };

    return (
      <LoopFieldsSet
        title="Task Management"
        data={formData.tasks}
        onAdd={addTask}
        onRemove={removeTask}
        showBuiltInAddButton={false}
        showBuiltInRemoveButton={false}
        addRemoveButtonsPosition="both"
        gridCols="grid-cols-[1fr_1fr_auto_auto_auto_auto_auto]"
        renderCustomButtons={(position, task, index) => {
          if (position === "header") {
            return (
              <Button
                type="contained"
                color="primary"
                size="sm"
                onClick={addTask}
                disabled={formData.tasks.length >= 10}
              >
                Add New Task
              </Button>
            );
          }

          if (position === "inline" && task) {
            return (
              <>
                <Button
                  type="icon"
                  icon={
                    task.completed ? "check_circle" : "radio_button_unchecked"
                  }
                  variant="text"
                  color={task.completed ? "success" : "default"}
                  size="sm"
                  onClick={() => toggleComplete(task.id)}
                  title={task.completed ? "Mark Incomplete" : "Mark Complete"}
                />
                <Button
                  type="icon"
                  icon="keyboard_arrow_up"
                  variant="text"
                  color="default"
                  size="sm"
                  onClick={() => moveUp(index)}
                  disabled={index === 0}
                  title="Move Up"
                />
                <Button
                  type="icon"
                  icon="keyboard_arrow_down"
                  variant="text"
                  color="default"
                  size="sm"
                  onClick={() => moveDown(index)}
                  disabled={index === formData.tasks.length - 1}
                  title="Move Down"
                />
                <Button
                  type="icon"
                  icon="content_copy"
                  variant="text"
                  color="primary"
                  size="sm"
                  onClick={() => {
                    const duplicated = {
                      ...task,
                      id: uuidv4(),
                      completed: false,
                    };
                    setFormData({
                      ...formData,
                      tasks: [...formData.tasks, duplicated],
                    });
                  }}
                  disabled={formData.tasks.length >= 5}
                  title="Duplicate"
                />
                <Button
                  type="icon"
                  icon="delete"
                  variant="text"
                  color="error"
                  size="sm"
                  onClick={() => removeTask(task.id)}
                  disabled={formData.tasks.length <= 1}
                  title="Delete"
                />
              </>
            );
          }
          return null;
        }}
        renderFields={(task, index) => (
          <>
            <FormFieldSet
              type="text"
              title="Task Title"
              value={task.title || ""}
              onChange={(value) => handleChange(`tasks[${index}].title`, value)}
            />
            <FormFieldSet
              type="select"
              title="Priority"
              options={[
                { label: "Low", value: "low" },
                { label: "Medium", value: "medium" },
                { label: "High", value: "high" },
                { label: "Critical", value: "critical" },
              ]}
              value={task.priority || "medium"}
              onChange={(value) =>
                handleChange(`tasks[${index}].priority`, value)
              }
            />
          </>
        )}
      />
    );
  };

  return (
    <div className="w-full p-6 space-y-8  overflow-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          LoopFieldsSet Component Examples
        </h1>
        <p className="text-gray-600 mb-6">
          This page demonstrates all possible configurations and usage patterns
          of the LoopFieldsSet component.
        </p>
      </div>

      {/* Example 1: Education Details */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          1. Education Details (Standard Configuration)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Features: Standard add/remove buttons, validation, min/max items,
          custom grid layout
        </p>
        <EducationExample />
      </div>

      {/* Example 2: Work Experience */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          2. Work Experience (Complex Fields)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Features: Multiple field types, date fields, textarea, different
          limits
        </p>
        <WorkExperienceExample />
      </div>

      {/* Example 3: Custom Buttons */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          3. Skills (Custom Buttons)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Features: Custom buttons, duplicate functionality, inline button
          positioning
        </p>
        <CustomButtonsExample />
      </div>

      {/* Example 4: Minimal Configuration */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          4. Simple Items (Minimal Configuration)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Features: Minimal props, single field, default settings
        </p>
        <MinimalExample />
      </div>

      {/* Example 5: Header-only Buttons */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          5. Emergency Contacts (Header-only Buttons)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Features: Header-only button positioning, custom add button title,
          2-column grid
        </p>
        <HeaderButtonsExample />
      </div>

      {/* Example 6: Advanced Custom Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          6. Task Management (Advanced Custom Actions)
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Features: No built-in buttons, multiple custom actions (complete,
          move, duplicate, delete), complex grid layout
        </p>
        <CustomActionsExample />
      </div>

      {/* Configuration Reference */}
      <div className="bg-gray-50 p-6 rounded-lg border">
        <h2 className="text-xl font-semibold mb-4 text-gray-800">
          Configuration Reference
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium mb-2">Core Props</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>
                <code>title</code> - Section title
              </li>
              <li>
                <code>data</code> - Array of items
              </li>
              <li>
                <code>onAdd</code> - Add item function
              </li>
              <li>
                <code>onRemove</code> - Remove item function
              </li>
              <li>
                <code>renderFields</code> - Field rendering function
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-2">Customization Props</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>
                <code>minItems</code> - Minimum items (default: 0)
              </li>
              <li>
                <code>maxItems</code> - Maximum items (default: 10)
              </li>
              <li>
                <code>gridCols</code> - Grid layout classes
              </li>
              <li>
                <code>addButtonTitle</code> - Custom add button text
              </li>
              <li>
                <code>className</code> - Additional CSS classes
              </li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-2">Button Control Props</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>
                <code>showBuiltInAddButton</code> - Show/hide add button
              </li>
              <li>
                <code>showBuiltInRemoveButton</code> - Show/hide remove button
              </li>
              <li>
                <code>addRemoveButtonsPosition</code> - "header", "inline",
                "both"
              </li>
              <li>
                <code>renderCustomButtons</code> - Custom button renderer
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoopFormFieldsList;
